import { useState, useEffect } from 'react';
import './ChatSupport.css';
import { Phone, User } from 'lucide-react';
import { chatAPI } from '../../../config/chat-api';
import { IS_ALFAJR } from '../../../constants/general';

const CHAT_SUBTYPES = [
  { value: '', label: 'All' },
  { value: 'product_enquiry', label: 'Product Enquiry' },
  { value: 'issue_with_app', label: 'Issue with App' },
  { value: 'order_related', label: 'Order Related' },
  { value: 'missing_items', label: 'Missing Items' },
  { value: 'others', label: 'Others' },
];

const ChatSupport = ({ onChatSelect, selectedChatId, refreshTrigger }) => {
  const [chats, setChats] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [filters, setFilters] = useState({
    status: 'active',
    topic: '',
    subtype: '',
  });

  const fetchChats = async (page = 1, status = '', topic = '', subtype = '') => {
    try {
      setChatLoading(true);
      setChatError(null);

      const response = await chatAPI.getAllChats(page, 20, status, topic, subtype);

      if (response.success) {
        const data = response.data;
        setChats(data.list || []);
        const pag = data.pagination || {};
        setPagination({
          currentPage: pag.page || 1,
          totalPages: pag.pages || 1,
          totalItems: pag.total || 0,
          hasNext: (pag.page || 1) < (pag.pages || 1),
          hasPrev: (pag.page || 1) > 1,
        });
      } else {
        setChatError(response.error || 'Failed to fetch chats');
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
      setChatError('Error fetching chats: ' + error.message);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    fetchChats(currentPage, filters.status, filters.topic, filters.subtype);
  }, [currentPage]);

  useEffect(() => {
    if (!refreshTrigger?.count) return;

    if (refreshTrigger.reset) {
      const defaultFilters = { status: 'active', topic: '', subtype: '' };
      setFilters(defaultFilters);
      setCurrentPage(1);
      fetchChats(1, defaultFilters.status, defaultFilters.topic, defaultFilters.subtype);
    } else {
      fetchChats(currentPage, filters.status, filters.topic, filters.subtype);
    }
  }, [refreshTrigger]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchChats(1, filters.status, filters.topic, filters.subtype);
  };

  const handleRefresh = () => {
    fetchChats(currentPage, filters.status, filters.topic, filters.subtype);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getStatusClass = (status) => {
    if (status === 'active') return 'chat-status-active';
    if (status === 'closed') return 'chat-status-closed';
    return 'chat-status-default';
  };

  const getLastMessageSenderLabel = (sender) => {
    if (!sender) return '';
    return sender === 'User' ? 'User: ' : 'Admin: ';
  };

  const handleChatClick = (chat) => {
    if (onChatSelect) onChatSelect(chat);
  };

  return (
    <div className="chat-page">

      <div className="chat-page-header">
        <div className="chat-page-title">
          <h1>Chat Support</h1>
          <p>View and manage customer support conversations</p>
        </div>
        <button
          className="chat-refresh-btn"
          onClick={handleRefresh}
          disabled={chatLoading}
        >
          Refresh
        </button>
      </div>


      <div className="chat-filter-section">
        <div className="chat-filter-form">
          <div className="chat-filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="chat-filter-select"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="chat-filter-group">
            <label>Topic</label>
            <select
              value={filters.topic}
              onChange={(e) => handleFilterChange('topic', e.target.value)}
              className="chat-filter-select"
            >
              <option value="">All</option>
              <option value="order_related">Order Related</option>
              <option value="general">General</option>
            </select>
          </div>

          {!IS_ALFAJR && (
            <div className="chat-filter-group">
              <label>Subtype</label>
              <select
                value={filters.subtype}
                onChange={(e) => handleFilterChange('subtype', e.target.value)}
                className="chat-filter-select"
              >
                {CHAT_SUBTYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button onClick={applyFilters} className="chat-apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>


      {chatLoading ? (
        <div className="chat-loading-state">
          <div className="chat-loading-spinner" />
          <p>Loading chats...</p>
        </div>
      ) : chatError ? (
        <div className="chat-error-state">
          <p>{chatError}</p>
          <button
            className="chat-retry-button"
            onClick={() =>
              fetchChats(currentPage, filters.status, filters.topic, filters.subtype)
            }
          >
            Retry
          </button>
        </div>
      ) : chats.length === 0 ? (
        <div className="chat-empty-state">
          <p>No chats found</p>
        </div>
      ) : (
        <>
          <div className="chat-cards-grid">
            {chats.map((chat) => (
              <div
                key={chat._id}
                className={`chat-card ${selectedChatId === chat._id ? 'chat-card-selected' : ''}`}
                onClick={() => handleChatClick(chat)}
                style={{ cursor: 'pointer' }}
              >
                <div className="chat-card-header">
                  <h3 className="chat-card-title">
                    {chat.title || 'Support Chat'}
                  </h3>
                  <span className={`chat-card-status ${getStatusClass(chat.status)}`}>
                    {chat.status
                      ? chat.status.charAt(0).toUpperCase() + chat.status.slice(1)
                      : '-'}
                  </span>
                </div>

                <div className="chat-card-user">
                  <User size={16} className="chat-user-icon" />
                  {chat.userName || 'Unknown User'}
                </div>
                {chat.userPhone && (
                  <div className="chat-card-phone">
                    <Phone size={16} className="chat-user-icon" />
                    {chat.userPhone}
                  </div>
                )}

                {chat.lastMessage && (
                  <div className="chat-card-last-message">
                    {chat.lastMessageSender && (
                      <span className="sender-label">
                        {getLastMessageSenderLabel(chat.lastMessageSender)}
                      </span>
                    )}
                    {chat.lastMessage}
                  </div>
                )}

                <div className="chat-card-footer">
                  <span className="chat-card-time">{chat.time || ''}</span>
                  <div className="chat-card-badges">
                    {chat.unreadCount > 0 && (
                      <span className="chat-badge chat-badge-unread">
                        {chat.unreadCount} unread
                      </span>
                    )}
                    {chat.status === 'closed' && chat.closedBy && (
                      <span className="chat-badge-closed-by">
                        Closed by {chat.closedBy}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="chat-pagination">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev || chatLoading}
                className="chat-pagination-button"
              >
                Previous
              </button>
              <span className="chat-pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext || chatLoading}
                className="chat-pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatSupport;